/**
 * AgentIcon Component
 * 
 * Displays an icon/avatar for an Eve agent.
 * Shows the agent's icon with optional badge and tooltip.
 */

import { getAgentIcon, getAgentDisplayName, getAgentColorClass } from "@/utils/agent-icons";
import { cx } from "@/utils/cx";

export interface AgentIconProps {
    /**
     * Agent name from backend (e.g., 'eve_care', 'EveCare')
     */
    agentName: string | null | undefined;
    
    /**
     * Size of the icon
     */
    size?: "xs" | "sm" | "md" | "lg";
    
    /**
     * Show agent name as tooltip or label
     */
    showLabel?: boolean;
    
    /**
     * Additional CSS classes
     */
    className?: string;
    
    /**
     * Show badge indicator (e.g., for active agents)
     */
    showBadge?: boolean;
    
    /**
     * Badge color
     */
    badgeColor?: "blue" | "green" | "purple" | "orange" | "red" | "gray";
}

const sizeClasses = {
    xs: "size-3",
    sm: "size-4",
    md: "size-5",
    lg: "size-6",
};

const badgeSizeClasses = {
    xs: "size-1.5",
    sm: "size-2",
    md: "size-2.5",
    lg: "size-3",
};

const badgeColorClasses = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
    red: "bg-red-500",
    gray: "bg-gray-500",
};

export function AgentIcon({
    agentName,
    size = "md",
    showLabel = false,
    className,
    showBadge = false,
    badgeColor = "blue",
}: AgentIconProps) {
    const Icon = getAgentIcon(agentName);
    const displayName = getAgentDisplayName(agentName);
    const colorClass = getAgentColorClass(agentName);
    
    return (
        <div className={cx("relative inline-flex items-center gap-2", className)}>
            <div className="relative">
                <Icon 
                    className={cx(
                        sizeClasses[size],
                        colorClass,
                        "flex-shrink-0"
                    )} 
                    aria-label={displayName}
                />
                {showBadge && (
                    <span
                        className={cx(
                            "absolute -top-0.5 -right-0.5 rounded-full border-2 border-white",
                            badgeSizeClasses[size],
                            badgeColorClasses[badgeColor]
                        )}
                        aria-label="Active"
                    />
                )}
            </div>
            {showLabel && (
                <span className="text-sm font-medium text-primary">
                    {displayName}
                </span>
            )}
        </div>
    );
}

